/**
 * Subtitle Helper Utilities
 * 
 * Functions for validating, fixing, and manipulating subtitles.
 */

import { Subtitle } from '../shared/types';

/**
 * Fix overlapping/tight subtitles by ensuring a minimal visual gap between
 * consecutive entries. This avoids "merged" rendering artifacts at runtime.
 */
export const fixSubtitleOverlaps = (subtitles: Subtitle[], minGapSeconds = 0.06): Subtitle[] => {
    if (!subtitles || subtitles.length === 0) return [];

    // Separate visible and hidden subtitles to avoid hidden ones clamping visible ones
    const visibleSubs = subtitles.filter(s => !s.hidden);
    const hiddenSubs = subtitles.filter(s => s.hidden);

    const sorted = [...visibleSubs].sort((a, b) => a.start - b.start);
    const fixed: Subtitle[] = [];
    for (let i = 0; i < sorted.length; i++) {
        const current = { ...sorted[i] };
        if (i < sorted.length - 1) {
            const next = sorted[i + 1];
            const latestAllowedEnd = next.start - minGapSeconds;
            if (current.end > latestAllowedEnd) {
                current.end = Math.max(current.start, latestAllowedEnd);
            }
        }
        fixed.push(current);
    }

    // Reintegrate hidden subs and sort everything back by start time
    return [...fixed, ...hiddenSubs].sort((a, b) => a.start - b.start);
};

/**
 * Check if there are any overlapping subtitles
 */
export const hasSubtitleOverlaps = (subtitles: Subtitle[]): boolean => {
    if (!subtitles || subtitles.length < 2) return false;
    const sorted = [...subtitles].sort((a, b) => a.start - b.start);
    for (let i = 0; i < sorted.length - 1; i++) {
        if (sorted[i].end > sorted[i + 1].start) return true;
    }
    return false;
};

// ─── Timing Shift & Filter Utilities ───────────────────────────────

/**
 * Shift all subtitle timecodes by `delta` seconds.
 */
export const shiftSubtitles = (subs: Subtitle[], delta: number): Subtitle[] => {
    if (delta === 0 || !subs?.length) return subs;
    return subs.map(sub => ({
        ...sub,
        start: sub.start + delta,
        end: sub.end + delta,
    }));
};

/**
 * Filter subtitles to those overlapping [minTime, maxTime], clamping boundaries.
 */
export const filterSubtitlesByRange = (subs: Subtitle[], minTime: number, maxTime: number): Subtitle[] => {
    return subs
        .filter(sub => sub.end > minTime && sub.start < maxTime)
        .map(sub => ({
            ...sub,
            start: Math.max(sub.start, minTime),
            end: Math.min(sub.end, maxTime),
        }));
};

/**
 * Apply a time shift to all subtitle languages on a clip.
 */
export const shiftAllSubtitleLanguages = (
    subtitles: Record<string, Subtitle[]> | undefined,
    delta: number
): Record<string, Subtitle[]> | undefined => {
    if (!subtitles || delta === 0) return subtitles;
    return Object.fromEntries(
        Object.entries(subtitles).map(([lang, subs]) => [lang, shiftSubtitles(subs, delta)])
    );
};

/**
 * Filter subtitles for all languages to a given time range.
 */
export const filterAllSubtitleLanguages = (
    subtitles: Record<string, Subtitle[]> | undefined,
    minTime: number,
    maxTime: number
): Record<string, Subtitle[]> | undefined => {
    if (!subtitles) return subtitles;
    const result = Object.fromEntries(
        Object.entries(subtitles).map(([lang, subs]) => [lang, filterSubtitlesByRange(subs, minTime, maxTime)])
    );
    return Object.values(result).some(subs => subs.length > 0) ? result : undefined;
};
